// Swap an avatar's base-colour map for a "look" texture.
//
// Shared by the world renderer and the wardrobe preview. It lived inside
// FixedRetargetedAvatar first, which meant applying a look changed the avatar
// in-world but not the card you clicked it from — so the feature appeared to do
// nothing at the exact moment the user was looking for feedback.
//
// The material is cloned before it is touched. useGLTF caches by URL, so two
// components showing the same avatar share one material instance; writing the
// map directly would repaint every one of them, including other players wearing
// that base model.

import { applyLookMap, isolateLookMaterials } from '../avatar/lookMaterials';
import { useEffect } from 'react';
import * as THREE from 'three';
import { fetchPossiblyCorsBlocked, isMeshyAsset } from '../services/avatarLookService';

export function useLookTexture(root: THREE.Object3D | undefined | null, textureUrl: string | null) {
  useEffect(() => {
    if (!root) return;
    let cancelled = false;
    let loaded: THREE.Texture | null = null;

    if (!textureUrl) return;
    const materials = isolateLookMaterials(root);
    const { targets } = materials;

    // Older look rows can still hold a Meshy URL (stored before the durable
    // copy landed, or when the copy fell back). Those hosts send no CORS
    // headers, so they are fetched as a blob through the server-side proxy and
    // loaded from an object URL — which also heals existing rows without
    // touching the database.
    let objectUrl: string | null = null;
    (async () => {
      let src = textureUrl;
      if (isMeshyAsset(textureUrl)) {
        const res = await fetchPossiblyCorsBlocked(textureUrl);
        if (!res.ok) throw new Error(`proxy fetch ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        src = objectUrl;
      }
      if (cancelled) return;
      new THREE.TextureLoader().load(
        src,
        (tex) => {
          if (cancelled) { tex.dispose(); return; }
          // Must match how glTF authors base-colour maps, or the avatar
          // renders noticeably darker and the UVs land upside down.
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.flipY = false;
          tex.needsUpdate = true;
          loaded = tex;
          targets.forEach((mat) => applyLookMap(mat, tex, mat.map));
        },
        undefined,
        (err) => {
          // Loudly. A silent handler here is what made a working generation
          // look like a dead feature for several rounds of debugging.
          console.error('[looks] texture failed to load', textureUrl, err);
        },
      );
    })().catch((err) => console.error('[looks] texture fetch failed', textureUrl, err));

    return () => {
      cancelled = true;
      materials.dispose();
      loaded?.dispose();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [root, textureUrl]);
}

export default useLookTexture;
